import React, { useState, useEffect, useRef } from 'react';
import { API, authFetch } from '../utils/api';
import { docReqSummary } from './CancelAgencyDocReqSheet';

// 대행요청 '완료' 세그먼트 전용 장부형 목록.
// Jin 확정 시안 = scratchpad/redesign/done-list.html (블록 ①~④). 앱 RD 장부와 정합.
//
// 완료 탭은 "무엇을 처리할까" 가 아니라 "얼마나 끝냈나" 를 묻는 화면이라 트리아지 UI 가 전부 잡음이다.
// 그래서 상태 드롭다운·D+ 경과 배지·압류/자격 칩은 여기서 빼고(⋯ 메뉴로 이동), 대신
//   ① 기간 요약 스트립 ② (완료일 × 업체) 그룹 밴드 ③ 34px 고밀도 행 을 둔다.
// 신규/진행중/보류 세그먼트는 종전 표 그대로 — CancelAgency.js 가 seg==='done' 일 때만 이 컴포넌트를 쓴다.

// ── 순수 함수 (계약은 __tests__/CancelAgencyRequests.test.js) ─────────────────

// 접수→말소 처리일수. 신청일·완료일이 둘 다 유효할 때만 값이 나온다.
// 음수(완료일이 신청일보다 앞)는 데이터 오류라 평균을 흔들지 않게 제외한다.
export function doneDays(r) {
  var a = String((r && r.requestDate) || '').slice(0, 10);
  var b = String((r && r.completedDate) || '').slice(0, 10);
  if (!a || !b) return null;
  var ta = new Date(a + 'T00:00:00').getTime();
  var tb = new Date(b + 'T00:00:00').getTime();
  if (isNaN(ta) || isNaN(tb)) return null;
  var d = Math.round((tb - ta) / 86400000);
  return d < 0 ? null : d;
}

// 기간 요약 — 건수 / 수수료 합계 / 평균 처리일(소수 1자리).
// 평균은 산출 가능한 행만으로 낸다(avgBase = 그 모수) — 날짜 빈 행이 0일로 섞여 평균을 낮추면 거짓말이 된다.
export function doneSummary(rows) {
  var list = rows || [];
  var fee = 0, sum = 0, base = 0;
  list.forEach(function (r) {
    fee += parseInt(r && r.fee, 10) || 0;
    var d = doneDays(r);
    if (d != null) { sum += d; base++; }
  });
  return {
    count: list.length,
    fee: fee,
    avgDays: base ? Math.round((sum / base) * 10) / 10 : null,
    avgBase: base
  };
}

// (완료일 × 업체) 그룹. 들어온 순서(기본 정렬 = 완료일 내림차순)를 그대로 따르되
// 같은 키가 떨어져 있어도 한 밴드로 합친다 — 장부에서 같은 날 같은 업체가 두 번 나오면 소계가 못 읽힌다.
// (표 쪽 reqGroupRows 는 '연속 구간' 규칙이라 일부러 다르다 — 그쪽은 정렬을 건드리지 않는 구분 행이다.)
export function groupDoneRows(rows, nameOf) {
  var pick = nameOf || function (r) { return (r && r.agencyName) || '미지정'; };
  var order = [], map = {};
  (rows || []).forEach(function (r) {
    var date = String((r && r.completedDate) || '').slice(0, 10);
    var company = pick(r);
    var key = date + '\u0000' + company;
    if (!map[key]) {
      map[key] = { key: key, date: date, company: company, rows: [], fee: 0 };
      order.push(map[key]);
    }
    map[key].rows.push(r);
    map[key].fee += parseInt(r && r.fee, 10) || 0;
  });
  return order;
}

var WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

// 밴드 날짜 라벨 — '8월 13일 (수)'. 완료일이 비면 '완료일 미상'.
export function doneBandLabel(iso) {
  var v = String(iso || '').slice(0, 10);
  if (!v) return '완료일 미상';
  var d = new Date(v + 'T00:00:00');
  if (isNaN(d.getTime())) return v;
  return (d.getMonth() + 1) + '월 ' + d.getDate() + '일 (' + WEEKDAY[d.getDay()] + ')';
}

// 행의 말소일 표기 — 'M/D'
function shortDate(iso) {
  var v = String(iso || '').slice(0, 10);
  var d = new Date(v + 'T00:00:00');
  if (!v || isNaN(d.getTime())) return '-';
  return (d.getMonth() + 1) + '/' + d.getDate();
}

var fmt = function (n) { return (parseInt(n, 10) || 0).toLocaleString(); };

// 좁은 폭에서 한 줄 유지 — 줄바꿈 대신 말줄임 (모바일 카드 전용)
var ELLIP = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

// ── 화면 ──────────────────────────────────────────────────────────────────

function CancelAgencyDoneList(props) {
  var rows = props.rows || [];
  var isStaff = !!props.isStaff;
  var isMobile = !!props.isMobile;
  var docList = props.docList || [];
  var nameOf = props.nameOf || function (r) { return (r && r.agencyName) || '미지정'; };
  var tapBtn = isMobile ? { minHeight: 40 } : undefined;
  var tapRowBtn = isMobile ? { minHeight: 40, minWidth: 40, justifyContent: 'center' } : undefined;

  var [collapsed, setCollapsed] = useState({});
  var [menuId, setMenuId] = useState(null);
  var toggleBand = function (key) {
    setCollapsed(function (p) {
      var n = Object.assign({}, p);
      if (n[key]) delete n[key]; else n[key] = true;
      return n;
    });
  };

  // 헤더 정렬을 고른 동안에는 밴드를 접고 평면 목록으로 — 표 쪽 관례(CancelAgency.js 의 showDateSep) 와 같다.
  // 완료일 아닌 열로 정렬하면 밴드가 목록보다 많아진다.
  var flat = !!props.sortKey;
  var groups = flat ? null : groupDoneRows(rows, nameOf);
  var sum = doneSummary(rows);
  // 표 열 수 = 차량·차대번호·소유자·말소일·[수수료]·말소증·서류·액션 (밴드 행 colSpan / 빈 상태 colSpan 공용)
  var cols = isStaff ? 7 : 8;

  // 말소증 존재 여부 — 완료 행 id 를 모아 배치 1회(서버 상한 200 → 청크). 이 컴포넌트는
  // 완료 세그먼트에서만 마운트되므로(CancelAgency.js seg==='done') 다른 탭에선 요청이 안 나간다.
  // null = 미조회 → 배지를 아예 그리지 않는다(있다가 사라지는 깜빡임 방지).
  var [certFlags, setCertFlags] = useState(null);
  // 키는 **집합**이지 순서가 아니다 — 정렬만 바꿔도 키가 달라지면 헤더를 누를 때마다 같은 조회가 다시 나간다.
  var idKey = rows.map(function (r) { return r.id; }).sort(function (a, b) { return a - b; }).join(',');
  useEffect(function () {
    var ids = idKey ? idKey.split(',') : [];
    if (!ids.length) { setCertFlags(null); return undefined; }
    var alive = true;
    var chunks = [];
    for (var i = 0; i < ids.length; i += 200) chunks.push(ids.slice(i, i + 200));
    Promise.all(chunks.map(function (c) {
      return authFetch(API + '/cancel-requests/malso-cert-flags?ids=' + c.join(','))
        .then(function (res) { return res.ok ? res.json() : null; })
        .catch(function () { return null; });
    })).then(function (list) {
      if (!alive) return;
      var merged = {};
      list.forEach(function (d) {
        var f = d && d.flags;
        if (f) Object.keys(f).forEach(function (k) { merged[k] = f[k] === true; });
      });
      setCertFlags(merged);
    });
    return function () { alive = false; };
  }, [idKey]);

  // 말소증 원본 뷰어 — 인증 필요라 <a href>/새 탭은 401. authFetch blob → objectURL (BulkCancelUpload 와 같은 방식)
  var [cert, setCert] = useState(null);
  var [certBusy, setCertBusy] = useState(0);
  // 열어 둔 채 화면을 떠나면(세그먼트 전환·탭 이동) closeCert 가 안 불려 blob 이 문서 수명 내내 남는다.
  var certUrlRef = useRef('');
  useEffect(function () {
    return function () { if (certUrlRef.current) { try { URL.revokeObjectURL(certUrlRef.current); } catch (e) { /* 이미 해제됨 */ } certUrlRef.current = ''; } };
  }, []);
  var openCert = function (r) {
    if (certBusy) return;
    setCertBusy(r.id);
    var ctype = '';
    authFetch(API + '/cancel-requests/' + r.id + '/malso-cert').then(function (res) {
      if (!res.ok) throw new Error(res.status === 404 ? 'none' : 'fail');
      ctype = res.headers.get('Content-Type') || '';
      return res.blob();
    }).then(function (b) {
      setCertBusy(0);
      var u = URL.createObjectURL(b);
      certUrlRef.current = u;
      setCert({ id: r.id, carNo: r.carNo, url: u, pdf: ctype.indexOf('application/pdf') >= 0 });
    }).catch(function (e) {
      setCertBusy(0);
      var none = e && e.message === 'none';
      if (props.showToast) props.showToast(none ? '말소증이 없습니다' : '말소증을 불러오지 못했습니다', 'err');
    });
  };
  var closeCert = function () {
    if (cert && cert.url) { try { URL.revokeObjectURL(cert.url); } catch (e) { /* 이미 해제됨 */ } }
    certUrlRef.current = '';
    setCert(null);
  };

  // 말소증 배지 / 서류 배지 / ⋯ 메뉴 — 표 행과 모바일 카드가 공유(핸들러 드리프트 방지)
  var certBtn = function (r) {
    if (!certFlags || certFlags[r.id] !== true) return null;
    return (
      <button className="btn btn-outline btn-sm" onClick={function () { openCert(r); }} title="말소증 원본 보기"
        style={Object.assign({ fontSize: 11, background: '#ecfdf5', borderColor: '#6ee7b7', color: '#047857', fontWeight: 700 }, tapRowBtn)}>{certBusy === r.id ? '…' : '말소증'}</button>
    );
  };
  var docBtn = function (r) {
    var db = docReqSummary(docList, r.carId);
    return (
      <button className="btn btn-outline btn-sm" onClick={function () { props.onOpenDocs(r); }} title={db.title}
        style={Object.assign({ fontSize: 11 }, tapRowBtn, db.tone === 'blue' ? { background: '#eff6ff', borderColor: '#93c5fd', color: '#1d4ed8', fontWeight: 700 } : null)}>{db.label}</button>
    );
  };
  var moreMenu = function (r) {
    return (
      <span className="cancel-row-actions" style={{ position: 'relative', display: 'inline-block' }}>
        <button className="btn btn-outline btn-sm" title="더보기" style={tapRowBtn} onClick={function () { setMenuId(menuId === r.id ? null : r.id); }}>⋯</button>
        {menuId === r.id && (<div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, boxShadow: '0 10px 26px rgba(15,23,42,.16)', zIndex: 50, minWidth: 158, padding: 5, textAlign: 'left' }}>
          <button className="btn btn-sm" style={Object.assign({ display: 'block', width: '100%', marginBottom: 4, textAlign: 'left', border: 'none', background: 'transparent' }, tapBtn)} onClick={function () { setMenuId(null); props.onRevert(r); }}>↺ 완료 되돌리기</button>
          {!isStaff && <button className="btn btn-sm" style={Object.assign({ display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent' }, tapBtn)} onClick={function () { setMenuId(null); props.onEdit(r); }}>수정</button>}
        </div>)}
      </span>
    );
  };
  // 밴드 안쪽(날짜 · 업체 … N건 · 소계) — 표의 구분 행과 모바일 밴드가 공유
  var bandInner = function (g, open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10, color: '#a8b4c4', width: 10 }}>{open ? '▾' : '▸'}</span>
        <span className="dl-num" style={Object.assign({ fontSize: 11.5, fontWeight: 700, color: '#64748b' }, isMobile ? { whiteSpace: 'nowrap' } : null)}>{doneBandLabel(g.date)}</span>
        <span style={{ color: '#c3d0df', fontSize: 11 }}>·</span>
        <span style={Object.assign({ fontSize: 13, fontWeight: 800, color: '#1B3A5C', letterSpacing: '-.2px' }, isMobile ? Object.assign({ minWidth: 0 }, ELLIP) : null)}>{g.company}</span>
        <span style={{ flex: 1, height: 1, background: '#dde7f2', margin: '0 4px' }}></span>
        <span className="dl-num" style={Object.assign({ fontSize: 11.5, fontWeight: 700, color: '#5b6b7f' }, isMobile ? { whiteSpace: 'nowrap' } : null)}>{g.rows.length}건</span>
        {!isStaff && <React.Fragment>
          <span style={{ color: '#c3d0df', fontSize: 11 }}>·</span>
          <span className="dl-num" style={{ fontSize: 12.5, fontWeight: 800, color: '#0f5132', minWidth: 76, textAlign: 'right' }}>{fmt(g.fee)}원</span>
        </React.Fragment>}
      </div>
    );
  };

  // 모바일 카드 1장 — 표(7열 · 가로 스크롤 760)를 390px 에서 읽을 수 있게 3줄로 접는다
  var renderCard = function (r) {
    var days = doneDays(r);
    return (
      <div key={r.id} id={'cancel-req-row-' + r.id} style={{ background: '#fff', border: '1px solid #eef2f7', borderRadius: 10, padding: '10px 12px', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', letterSpacing: '-.2px' }} className="dl-num">{r.carNo || '-'}</span>
          {r.cancelType === '폐차말소' && <span style={{ padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#fef2f2', color: '#b91c1c' }}>폐차</span>}
          {!isStaff && <span className="dl-num" style={{ marginLeft: 'auto', flexShrink: 0, fontSize: 13, fontWeight: 700, color: (parseInt(r.fee, 10) || 0) ? '#1f3d2c' : '#a8b4c4' }}>{fmt(r.fee)}원</span>}
        </div>
        <div style={Object.assign({ fontSize: 12, color: '#5b6b7f', marginTop: 3 }, ELLIP)}>{[r.carType, r.owner].filter(Boolean).join(' · ') || '-'}</div>
        <div className="dl-num" style={Object.assign({ fontSize: 11, color: '#a8b4c4', marginTop: 1 }, ELLIP)}>{r.vin || '-'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
          <span className="dl-num" style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
            말소 {shortDate(r.completedDate)}
            {days != null && <React.Fragment> · <b style={{ color: '#41536a', fontWeight: 700 }}>{days}일</b></React.Fragment>}
          </span>
          <span style={{ flex: 1 }} />
          {certBtn(r)}
          {docBtn(r)}
          {moreMenu(r)}
        </div>
      </div>
    );
  };

  var renderRow = function (r) {
    var days = doneDays(r);
    return (
      <tr key={r.id} id={'cancel-req-row-' + r.id}>
        <td>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', letterSpacing: '-.2px' }} className="dl-num">{r.carNo || '-'}</span>
          {r.cancelType === '폐차말소' && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: '#fef2f2', color: '#b91c1c' }}>폐차</span>}
          <span style={{ marginLeft: 7, color: '#5b6b7f' }}>{r.carType || ''}</span>
        </td>
        <td className="dl-num" style={{ color: '#a8b4c4', fontSize: 11.5 }}>{r.vin || '-'}</td>
        <td style={{ color: '#5b6b7f' }}>{r.owner || '-'}</td>
        <td className="dl-num" style={{ color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
          말소 {shortDate(r.completedDate)}
          {days != null && <React.Fragment> · <b style={{ color: '#41536a', fontWeight: 700 }}>{days}일</b></React.Fragment>}
        </td>
        {!isStaff && <td className="dl-num" style={{ textAlign: 'right', fontWeight: 700, color: (parseInt(r.fee, 10) || 0) ? '#1f3d2c' : '#a8b4c4' }}>{fmt(r.fee)}원</td>}
        <td>{certBtn(r)}</td>
        <td>{docBtn(r)}</td>
        <td style={{ textAlign: 'right' }}>{moreMenu(r)}</td>
      </tr>
    );
  };

  // 모바일 요약 타일 — 구분선으로 나눈 한 줄이 390px 에선 3줄로 접혀 읽히지 않는다 → 2×2 격자
  var mTile = function (label, value, unit, color, note) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e4edf8', borderRadius: 8, padding: '8px 10px', minWidth: 0 }}>
        <div style={Object.assign({ fontSize: 11, fontWeight: 700, color: '#64748b' }, ELLIP)}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
          <b className="dl-num" style={{ fontSize: 19, fontWeight: 800, color: color, letterSpacing: '-.5px' }}>{value}</b>
          <em style={{ fontStyle: 'normal', fontSize: 12, fontWeight: 700, color: '#475569' }}>{unit}</em>
          {!!note && <small style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600 }}>{note}</small>}
        </div>
      </div>
    );
  };

  return (<div>
    {/* ① 기간 요약 스트립 — 기간 필터 결과(rows) 기준. 필터를 바꾸면 이 수치도 같이 움직인다. */}
    {isMobile ? (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, background: 'linear-gradient(180deg,#fbfdff,#f4f8fd)', border: '1px solid #dbe6f3', borderRadius: 10, padding: 10, marginBottom: 8 }}>
      {mTile((props.periodLabel || '전체') + ' 완료', sum.count, '건', '#1B3A5C')}
      {!isStaff && mTile('수수료 합계', fmt(sum.fee), '원', '#0f5132')}
      <div style={isStaff ? undefined : { gridColumn: '1 / -1' }}>
        {mTile('평균 처리', sum.avgDays == null ? '—' : sum.avgDays, '일', '#1B3A5C', '접수→말소 ' + sum.avgBase + '건 기준')}
      </div>
    </div>
    ) : (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 8, background: 'linear-gradient(180deg,#fbfdff,#f4f8fd)', border: '1px solid #dbe6f3', borderRadius: 10, padding: '12px 18px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, paddingRight: 24, marginRight: 24, borderRight: '1px solid #e2eaf4', whiteSpace: 'nowrap' }}>
        <i style={{ fontStyle: 'normal', fontSize: 11.5, fontWeight: 700, color: '#64748b' }}>{props.periodLabel || '전체'} 완료</i>
        <b className="dl-num" style={{ fontSize: 20, fontWeight: 800, color: '#1B3A5C', letterSpacing: '-.5px' }}>{sum.count}</b>
        <em style={{ fontStyle: 'normal', fontSize: 12, fontWeight: 700, color: '#475569' }}>건</em>
      </div>
      {!isStaff && (<div style={{ display: 'flex', alignItems: 'baseline', gap: 7, paddingRight: 24, marginRight: 24, borderRight: '1px solid #e2eaf4', whiteSpace: 'nowrap' }}>
        <i style={{ fontStyle: 'normal', fontSize: 11.5, fontWeight: 700, color: '#64748b' }}>수수료 합계</i>
        <b className="dl-num" style={{ fontSize: 20, fontWeight: 800, color: '#0f5132', letterSpacing: '-.5px' }}>{fmt(sum.fee)}</b>
        <em style={{ fontStyle: 'normal', fontSize: 12, fontWeight: 700, color: '#475569' }}>원</em>
      </div>)}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, whiteSpace: 'nowrap' }}>
        <i style={{ fontStyle: 'normal', fontSize: 11.5, fontWeight: 700, color: '#64748b' }}>평균 처리</i>
        <b className="dl-num" style={{ fontSize: 20, fontWeight: 800, color: '#1B3A5C', letterSpacing: '-.5px' }}>{sum.avgDays == null ? '—' : sum.avgDays}</b>
        <em style={{ fontStyle: 'normal', fontSize: 12, fontWeight: 700, color: '#475569' }}>일</em>
        <small style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }} title={sum.avgBase + '건 기준 (신청일·완료일이 모두 있는 건)'}>접수→말소</small>
      </div>
    </div>
    )}

    {isMobile ? (
    /* 모바일 장부 — 밴드(접기 유지) + 3줄 카드. 표는 390px 에서 차량·차대번호만 보인다(실측 760/316). */
    <div>
      {flat && rows.map(renderCard)}
      {!flat && groups.map(function (g) {
        var open = !collapsed[g.key];
        return (<React.Fragment key={g.key}>
          <div onClick={function () { toggleBand(g.key); }}
            style={{ background: '#f3f7fc', border: '1px solid #e4ecf6', borderRadius: 8, padding: '9px 10px', margin: '10px 0 6px', cursor: 'pointer' }}>
            {bandInner(g, open)}
          </div>
          {open && g.rows.map(renderCard)}
        </React.Fragment>);
      })}
      {rows.length === 0 && (<div className="card" style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>완료된 대행 요청이 없습니다.</div>)}
    </div>
    ) : (
    <div className="card">
      <div style={isMobile ? { overflowX: 'auto', WebkitOverflowScrolling: 'touch' } : undefined}>
        <table className="data-table done-ledger sticky-head" style={isMobile ? { minWidth: 760 } : undefined}>
          <thead><tr>
            {props.sortTh('차량', 'carNo')}
            <th>차대번호</th>
            <th>소유자</th>
            {props.sortTh('말소일', 'completedDate')}
            {!isStaff && <th style={{ textAlign: 'right' }}>수수료</th>}
            {/* '말소증' = 업로드된 말소 증명 원본, '서류' = 수출업체에 요청한 보강서류 — 서로 다른 것이라 열을 나눈다 */}
            <th>말소증</th>
            <th>서류</th>
            <th></th>
          </tr></thead>
          <tbody>
            {flat && rows.map(renderRow)}
            {!flat && groups.map(function (g) {
              var open = !collapsed[g.key];
              return (<React.Fragment key={g.key}>
                {/* ② (완료일 × 업체) 그룹 밴드 — 캐럿으로 접기/펼치기, 우측에 건수·수수료 소계 */}
                <tr className="dl-band" onClick={function () { toggleBand(g.key); }}>
                  <td colSpan={cols}>{bandInner(g, open)}</td>
                </tr>
                {open && g.rows.map(renderRow)}
              </React.Fragment>);
            })}
            {rows.length === 0 && (<tr><td colSpan={cols} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>완료된 대행 요청이 없습니다.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
    )}

    {/* 하단 안내 1줄 — 시안의 페이징은 장식이라 옮기지 않는다(현행은 전량 로드). */}
    {!isStaff && (<div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 8, padding: '10px 14px', background: '#f8fbff', border: '1px solid #e2ebf6', borderRadius: 8, fontSize: 12, color: '#41536a' }}>
      <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#dbeafe', color: '#2563EB', fontSize: 10, fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>i</span>
      완료 건은 정산에 자동 반영됩니다. 금액 수정이 필요하면 행 ⋯ 메뉴에서 [수정] 하세요.
    </div>)}

    {/* 말소증 뷰어 — 닫을 때 objectURL 해제(누수 방지) */}
    {cert && (<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={closeCert}>
      <div style={{ background: '#fff', borderRadius: 12, padding: isMobile ? 14 : 20, width: 'min(900px,94vw)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={function (e) { e.stopPropagation(); }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? 16 : undefined }}>말소증 {cert.carNo ? '— ' + cert.carNo : ''}</h3>
          <button onClick={closeCert} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8', minWidth: isMobile ? 40 : undefined, minHeight: isMobile ? 40 : undefined }}>✕</button>
        </div>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: isMobile ? 220 : 320 }}>
          {cert.pdf
            ? <iframe title="말소증" src={cert.url} style={{ width: '100%', height: isMobile ? '60vh' : '72vh', border: 'none' }} />
            : <img src={cert.url} alt="말소증" style={{ maxWidth: '100%', maxHeight: isMobile ? '60vh' : '72vh', display: 'block' }} />}
        </div>
      </div>
    </div>)}
  </div>);
}

export default CancelAgencyDoneList;
